import { useMutation } from '@apollo/client'
import { graphql } from '@api/mesa'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useCallback } from 'react'
import { Routes } from '@/src/common/enums/routes'
import { router } from 'expo-router'
import { Platform } from 'react-native'
import { WebBrowserResultType } from 'expo-web-browser'
import { useRefreshPoints } from '@/src/common/hooks/usePoints'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'

export function useOpenRedemptionPortal() {
  const onError = useLogError()

  const [createMagicLinkToLogIntoRedemptionPortal] = useMutation(
    graphql(`
      mutation CreateMagicLinkToLogIntoRedemptionPortal($redirectPath: String!) {
        createMagicLinkToLogIntoRedemptionPortal(redirectPath: $redirectPath) {
          loginLink
        }
      }
    `),
    { onError },
  )

  const refreshPoints = useRefreshPoints()

  const openRedemptionPortal = useCallback(
    async (
      title: string,
      ...[requestVariables]: Parameters<typeof createMagicLinkToLogIntoRedemptionPortal>
    ) => {
      const [result] = await Promise.allSettled([
        createMagicLinkToLogIntoRedemptionPortal(requestVariables),
        // we can't open the browser until the backend fetches the url from ascenda and delivers it to the frontend,
        // but it's not immediately obvious that the user needs to wait for this to happen
        // (waiting for the browser to open is different than waiting for an already-opened browser to load a url).
        // let's impose an artificial wait time to give the user a chance to see the loading state
        new Promise((resolve) => setTimeout(resolve, 888)),
      ])

      if (result.status === 'rejected') {
        throw result.reason
      }

      if (result.value.data?.createMagicLinkToLogIntoRedemptionPortal.loginLink == null) {
        throw new Error('No login link was returned')
      }

      const loginLink = result.value.data.createMagicLinkToLogIntoRedemptionPortal.loginLink

      Platform.OS === 'web'
        ? void openInAppBrowser(loginLink, title, 'tab')?.then(({ type }) => {
            switch (type) {
              case WebBrowserResultType.CANCEL:
              case WebBrowserResultType.DISMISS:
              case WebBrowserResultType.LOCKED:
              case WebBrowserResultType.OPENED:
                void refreshPoints()
                break
            }
          })
        : router.push({
            pathname: Routes.Redemption.route,
            params: { title, loginLink },
          })
    },
    [createMagicLinkToLogIntoRedemptionPortal, refreshPoints],
  )

  return {
    openRedemptionPortal,
  } as const
}
